import { useContext, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import GlobalContextI from '../../interfaces/GlobalContext';
import GlobalContext from '../../contexts/GlobalContext';

/**
 * Full-screen DO NOT ADMIT page. Reached only when /api/scanner/parse
 * returns banned=true OR /api/guests/:type/find-by-license returns
 * { banned: true, reason }.
 *
 * DOB handling deliberately removed (revert 2026-05-07): no parseDob,
 * no age computation, no DOB display anywhere. Screen renders only
 * the minimum context staff needs to refuse admission:
 *   - First name (parsed from the scan, in current session memory)
 *   - Last 4 of license (small disambiguator)
 *   - Ban reason (operator context)
 *
 * The audit ledger captures only `idHashPrefix=<8 chars>` for these
 * events — no name, no DOB, no licenseID, no full hash.
 */
const BannedGuestPage = () => {
    const history = useHistory();
    const { guest, setGuest } = useContext<GlobalContextI>(GlobalContext);

    useEffect(() => {
        // Auto-clear after 30s if staff doesn't tap.
        const t = window.setTimeout(() => {
            setGuest(null);
            history.push('/kiosk');
        }, 30000);
        return () => window.clearTimeout(t);
    }, [history, setGuest]);

    const last4 =
        typeof guest?.licenseID === 'string' && guest.licenseID.length >= 4
            ? guest.licenseID.slice(-4)
            : '----';
    const first = (guest?.firstName || '').trim();
    const reason = (guest as any)?.bannedReason || '';

    const firstUpper = first ? first.toUpperCase() : 'GUEST';

    const acknowledge = () => {
        setGuest(null);
        history.push('/kiosk');
    };

    return (
        <div
            style={{
                position: 'fixed',
                inset: 0,
                background: '#1a0000',
                color: '#fff',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 32,
                textAlign: 'center',
                zIndex: 9999,
            }}
        >
            <div style={{ fontSize: '6rem', marginBottom: 8 }}>⛔</div>

            <h1
                style={{
                    color: '#ff2d2d',
                    fontSize: '5rem',
                    fontWeight: 900,
                    margin: '0 0 28px',
                    letterSpacing: 6,
                    lineHeight: 1,
                    textShadow: '0 0 24px rgba(255,45,45,0.5)',
                }}
            >
                DO NOT ADMIT
            </h1>

            <div
                style={{
                    fontSize: '1.9rem',
                    fontWeight: 700,
                    color: '#fff',
                    maxWidth: 920,
                    lineHeight: 1.35,
                    marginBottom: 24,
                }}
            >
                {firstUpper} is banned from the premises and is not allowed to enter.
            </div>

            {reason ? (
                <div
                    style={{
                        fontSize: '1.15rem',
                        fontWeight: 600,
                        color: '#ffd1d1',
                        background: 'rgba(255,45,45,0.12)',
                        border: '1px solid #ff5555',
                        borderRadius: 8,
                        padding: '10px 18px',
                        marginBottom: 28,
                        maxWidth: 720,
                    }}
                >
                    Reason: {reason}
                </div>
            ) : null}

            <div
                style={{
                    fontSize: '0.95rem',
                    color: '#ffaaaa',
                    marginBottom: 36,
                    fontFamily: 'monospace',
                    letterSpacing: 1,
                }}
            >
                Last 4 of ID: <strong>{last4}</strong>
            </div>

            <button
                onClick={acknowledge}
                style={{
                    background: '#3a1a1a',
                    color: '#fff',
                    border: '2px solid #ff5555',
                    borderRadius: 10,
                    fontSize: '1.2rem',
                    padding: '14px 36px',
                    fontWeight: 700,
                    letterSpacing: 1,
                    cursor: 'pointer',
                }}
            >
                Acknowledged — return to scanner
            </button>
        </div>
    );
};

export default BannedGuestPage;
